import { FilterViewData } from '@editorStores'

// Get all element types from the model store's allElementTypes
const getAllElementTypes = (): ElementTypes[] => {
  // These are the element types used in the model store
  return [
    'beams',
    'columns',
    'inner_walls',
    'lintels',
    'outer_walls',
    'purlins',
    'roof_slabs',
    'rips',
    'slabs',
    'vertical_roof_slabs',
    'vertical_slabs',
    'slab_beams',
    'roof_slab_beams',
    'standard_rips',
    'foundation',
  ] as ElementTypes[]
}

/**
 * Captures the current filter state into a FilterViewModel
 */
export const captureCurrentFilterView = (
  activeStorey: string | null,
  hiddenElementIds: Set<string>,
  visibleStoreys: Set<string>,
  visibilityByType: VisibilityByType,
  visibilityByTypeAndStorey: VisibilityByTypeAndStorey,
): FilterViewData => {
  // Convert visibilityByTypeAndStorey Sets to arrays
  const allElementTypes = getAllElementTypes()
  const byTypeStoreyVisibility = allElementTypes.reduce((acc, type) => {
    acc[type] = Array.from(visibilityByTypeAndStorey[type] || [])
    return acc
  }, {} as Record<ElementTypes, string[]>)
  const visibleStoreysArray = Array.from(visibleStoreys)
  const hiddenElementIdsArray = Array.from(hiddenElementIds)

  // Convert visibilityByType to the format we need
  const byTypeVisibility = { ...visibilityByType }

  return {
    hiddenElementIds: hiddenElementIdsArray,
    activeStorey,
    visibleStoreys: visibleStoreysArray,
    byTypeStoreyVisibility,
    byTypeVisibility,
  }
}

/**
 * Applies a FilterViewModel to the stores
 * If a value is null, it means "don't apply this filter" and the current store value is used
 */
export const applyFilterView = (
  filterView: FilterViewData,
  controlStoreMethods: {
    setAllElementsVisible: () => void
    addHiddenElementIds: (ids: string[]) => void
  },
  modelStoreMethods: {
    setVisibleStoreys: (storeys: string[]) => void
    setTypeVisibility: (type: ElementTypes, visible: boolean) => void
    setTypeVisibilityByStorey: (type: ElementTypes, storeys: string[]) => void
  },
) => {
  // Apply hiddenElementIds (only if not null)
  if (filterView.hiddenElementIds !== null) {
    controlStoreMethods.setAllElementsVisible()
    if (filterView.hiddenElementIds.length > 0) {
      controlStoreMethods.addHiddenElementIds(filterView.hiddenElementIds)
    }
  }

  // Apply visibleStoreys (only if not null)
  if (filterView.visibleStoreys !== null) {
    modelStoreMethods.setVisibleStoreys(filterView.visibleStoreys)
  }

  // Apply byTypeVisibility (only if not null)
  if (filterView.byTypeVisibility !== null) {
    ;(Object.entries(filterView.byTypeVisibility) as [ElementTypes, boolean][]).forEach(
      ([type, visible]) => {
        modelStoreMethods.setTypeVisibility(type, visible)
      },
    )
  }

  // Apply byTypeStoreyVisibility (only if not null)
  if (filterView.byTypeStoreyVisibility !== null) {
    ;(Object.entries(filterView.byTypeStoreyVisibility) as [ElementTypes, string[]][]).forEach(
      ([type, storeys]) => {
        modelStoreMethods.setTypeVisibilityByStorey(type, storeys)
      },
    )
  }
}
